import { useTranslation } from "react-i18next";
import { useId } from "react";
import { parsePhoneNumber } from "../../../common/util/phoneNumber";
import defaultContent from "../../../content/profile";
import "./ABOPhoneNumber.scss";
import { IconPhone } from "../../../common/icons/IconPhone/IconPhone";
import { IconMessage } from "../../../common/icons/IconMessage/IconMessage";
import { IconAddress } from "../../../common/icons/IconAddress/IconAddress";
import { useAppSelector, RootState } from "../../../store";
import LeafTooltip from "../../../common/leaf/LeafTooltip/LeafTooltip";
import { Button } from "../../../common/components/Button/Button";

type Props = {
  primaryNumber?: string;
  privacyFlag: boolean;
  applicantTrackOnclick: Function;
  id?: string;
};

const ABOPhoneNumber = (props: Props) => {
  const { t } = useTranslation(["profile"]);

  const {
    showCallIcon = false,
    showCopyIcon = false,
    showMessageIcon = false,
  } = useAppSelector((state: RootState) => state?.boot?.configuration?.profile);

  const { primaryNumber = "", privacyFlag, applicantTrackOnclick } = props;
  const showValidPhoneNumber = primaryNumber && primaryNumber !== "--" && primaryNumber !== " " && privacyFlag !== true;

  const { isValid: phoneNumberParsed, formattedNumber } = parsePhoneNumber(primaryNumber);
  const displayNumber = showValidPhoneNumber && phoneNumberParsed ? formattedNumber : "-";
  const uniqueId = useId();

  const _applicantTrackOnclick = (eventType: string) => {
    applicantTrackOnclick(eventType);
  };

  const copyTextToClipboard = () => {
    navigator.clipboard.writeText(displayNumber || "");
    _applicantTrackOnclick("mobile");
  };

  return (
    <div className="leaf-abo-phone-number">
      <div className="leaf-abo-phone-number__content">
        <div className="leaf-abo-phone-number__label">{`${t(
          "leaf.phoneNumber",
          defaultContent["leaf"]["phoneNumber"],
        )}`}</div>
        <div className="leaf-abo-phone-number__primary">{displayNumber}</div>
      </div>
      {showValidPhoneNumber && (
        <div className="leaf-abo-phone-number__icon">
          {showMessageIcon && (
            <a
              role="button"
              aria-label={`${t("leaf.sms", defaultContent["leaf"]["sms"])}`}
              href={`sms:${displayNumber}`}
              target="_parent"
              className="leaf-abo-phone-number__icon--message"
              onClick={() => _applicantTrackOnclick("message")}
            >
              <span aria-hidden="true">
                <IconMessage />
              </span>
            </a>
          )}
          {showCallIcon && (
            <a
              role="button"
              aria-label={`${t("leaf.phoneCall", defaultContent["leaf"]["phoneCall"])}`}
              className="leaf-abo-phone-number__icon--phone"
              href={`tel:${displayNumber}`}
              target="_parent"
              onClick={() => _applicantTrackOnclick("call")}
            >
              <span aria-hidden="true">
                <IconPhone />
              </span>
            </a>
          )}
          {showCopyIcon && (
            <Button
              aria-label={`${t("leaf.copyPhone", defaultContent["leaf"]["copyPhone"])}`}
              className="leaf-abo-phone-number__icon--copy"
              onClick={copyTextToClipboard}
            >
              <span aria-hidden="true">
                <LeafTooltip
                  id={uniqueId}
                  icon={<IconAddress />}
                  openOnClick={true}
                  content={`${t("leaf.copyPhoneTooltipText", defaultContent["leaf"]["copyPhoneTooltipText"])}`}
                />
              </span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ABOPhoneNumber;
